import { Card, CardContent, CardHeader, Divider, TextField } from "@mui/material";
import { List, ListItem, ListItemText, Switch } from "@mui/material";
import { useEffect, useState } from "react";
import { PasswordConfirmationDialog } from "../dialogs/PasswordConfirmationDialog";
import { CompanySetting, restoreCompanySettings, updateCompanySettings } from "../companySettingsAPI";
import { useAppDispatch } from "../../../../app/hooks";
import { showSuccess, showError } from "../../../notifications/notificationsSlice";
import { CardActionButtons } from "./CardActionButtons";
import { Localized } from "@fluent/react";
import { RestoreSettingsPasswordConfirmationDialog } from "../dialogs/RestoreSettingsPasswordConfirmationDialog";


interface CompanySessionSettingCardProps {
    companySettings: CompanySetting[];
    onUpdate: (updatedSettings: CompanySetting[]) => void;
}

export const CompanySessionSettingCard: React.FC<CompanySessionSettingCardProps> = ({ companySettings, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const dispatch = useAppDispatch()
    const [serverError, setServerError] = useState("")

    const originalSetting = companySettings.find(setting => setting.code === "SESSION_DURATION");
    const [sessionDurationSetting, setSessionDurationSetting] = useState<CompanySetting | undefined>(originalSetting);


    const isDefault = sessionDurationSetting?.isDefault === true;
    useEffect(() => {
        setSessionDurationSetting(originalSetting);
    }, [companySettings, isEditing]);



    const handleRestore = () => {
        setRestoreDialogOpen(true);
    };

    const handleSave = () => {
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setServerError("")
    };

    const handleCloseRestore = () => {
        setRestoreDialogOpen(false);
        setServerError("")
    };

    const handleConfirmRestore = async (password: string) => {
        if (password) {
            setSaving(true)
            setServerError("")

            try {
                const result =
                    await dispatch(restoreCompanySettings({
                        password: password,
                        settings: ["SESSION_DURATION"]
                    }))

                if (restoreCompanySettings.fulfilled.match(result)) {
                    const settings = result.payload as CompanySetting[];
                    onUpdate(settings);
                    setSessionDurationSetting(settings.find(f => f.code === "SESSION_DURATION"));
                    setRestoreDialogOpen(false);
                    setIsEditing(false);
                    dispatch(showSuccess("saved"))
                }
                else {
                    let errorMsg = "error"

                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }

    const handleSessionDurationLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let newValue = parseInt(e.target.value, 10) || 0;

        setSessionDurationSetting(prev => prev ? { ...prev, value: newValue.toString() } : undefined);
    };

    const handleConfirmSave = async (password: string) => {
        if (password && sessionDurationSetting) {
            setSaving(true)
            setServerError("")

            try {
                const updatedSettings: CompanySetting[] = [sessionDurationSetting];

                const result =
                    await dispatch(updateCompanySettings({
                        password: password,
                        settings: updatedSettings
                    }))

                if (updateCompanySettings.fulfilled.match(result)) {
                    const settings = result.payload as CompanySetting[];
                    onUpdate(settings)
                    setSessionDurationSetting(settings.find(f => f.code === "SESSION_DURATION"));
                    setDialogOpen(false);
                    setIsEditing(false);
                    dispatch(showSuccess("saved"))
                }
                else {
                    let errorMsg = "error"

                    if (result.payload) {
                        if (result.payload.kind === 'http') {
                            const problem = result.payload.problem
                            if (problem) {
                                errorMsg = problem.title
                            }
                        }
                    }
                    setServerError(errorMsg)
                }
            }
            catch (error) {
                dispatch(showError("error"))
            }
            finally {
                setSaving(false)
            }
        }
    }


    const handleCancel = () => {
        setIsEditing(false);
    };

    if (!sessionDurationSetting) {
        return null;
    }

    return (
        <Card sx={{ maxWidth: 500, borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
                title={<Localized id="company-settings-session-settings">
                    <span>_session-settings</span>
                </Localized>}
                sx={{
                    textAlign: "center",
                    bgcolor: "primary.main",
                    color: "white",
                    py: 1,
                    "& .MuiCardHeader-title": { fontSize: "1rem" },
                }}
            />
            <CardContent>
                <List>
                    <ListItem
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <ListItemText
                            sx={{ flex: 1, minWidth: 0 }}   // minWidth:0 pozwala poprawnie łamać/ucinać tekst
                            primary={
                                <Localized id="company-settings-session-duration-desc">
                                    <span>_session-duration-desc</span>
                                </Localized>
                            }
                        />

                        <TextField
                            type="number"
                            value={sessionDurationSetting?.value ?? ""}
                            onChange={handleSessionDurationLengthChange}
                            disabled={!isEditing}
                            variant="outlined"
                            size="small"
                            sx={{ width: 140 }} 
                            inputProps={{ min: 8, max: 50, step: 1 }}
                        />
                    </ListItem>


                </List>
                <Divider sx={{ width: "100%", mt: 3 }} />
                <CardActionButtons
                    isDefault={isDefault}
                    isEditing={isEditing}
                    onRestore={handleRestore}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onEdit={() => setIsEditing(true)}
                />

            </CardContent>
            {dialogOpen &&
                <PasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleClose}
                    onConfirm={handleConfirmSave}
                />
            }
            {restoreDialogOpen &&
                <RestoreSettingsPasswordConfirmationDialog
                    saving={saving}
                    serverError={serverError}
                    onClose={handleCloseRestore}
                    onConfirm={handleConfirmRestore}
                />
            }
        </Card>

    );
};
